import { get } from "./request";
import request from "./request";

/**
 * 第三方订单管理接口（API 文档第 12 节 /console/third-party-orders）。
 *
 * Enterprise 专属。控制台代理查询下游联盟订单（affiliate orders），
 * 下游返回结构原样透传给前端，不做字段裁剪。前端到下游的参数映射
 * （page/size → f/t、merchantOrderId → moi、from → st、to → et）由控制台
 * 网关完成，前端仅按文档发送 page/size/merchantOrderId/from/to。
 *
 * 列表响应：
 *   { "orders": [ { id, merchantOrderId, placedAt, amountCents, currency } ], "total": 42 }
 * 特殊情形（均返回 200）：
 *   - 下游查询失败：{ "orders": [], "total": 0, "error": "Failed to load third-party orders" }
 *   - 无下游会话凭证：{ "orders": [], "total": 0 }
 */

/** 每页条数上限（下游 t-f <= 50） */
export const MAX_PAGE_SIZE = 50;

/** 导出时间跨度上限（天）——对应后端 export.max_range_days 默认值 */
export const MAX_EXPORT_RANGE_DAYS = 32;

// ---------- 查询参数 ----------

export interface ThirdPartyOrdersQuery {
  /** 页码，默认 1（从 1 开始） */
  page?: number;
  /** 每页条数，默认 20，上限 50 */
  size?: number;
  /** 商家单号，精确匹配（可选） */
  merchantOrderId?: string;
  /** 下单时间起点（ISO-8601 instant，可选，按 placedAt >= 过滤） */
  from?: string;
  /** 下单时间终点（ISO-8601 instant，可选，按 placedAt <= 过滤） */
  to?: string;
}

// ---------- 响应类型（下游透传，字段与文档一致） ----------

export interface ThirdPartyOrder {
  /** 内部记录 UUID */
  id: string;
  /** 商家订单号 */
  merchantOrderId: string;
  /** 下单时间，UTC 毫秒 */
  placedAt: number;
  /** 订单金额，美分 */
  amountCents: number;
  /** 货币代码（ISO 4217，如 USD） */
  currency: string;
}

export interface ThirdPartyOrdersPage {
  /** 订单列表（按 placedAt 降序，由下游保证） */
  orders: ThirdPartyOrder[];
  /** 符合过滤条件的总记录数，用于分页 */
  total: number;
  /** 下游查询失败时的错误文案（成功时为 null） */
  error: string | null;
}

/** 下游透传的原始响应（仅声明用到的字段） */
interface RawThirdPartyOrdersResponse {
  orders?: ThirdPartyOrder[];
  total?: number;
  error?: string;
}

// ---------- 接口 ----------

/**
 * GET /third-party-orders：分页 + 筛选，返回下游透传的订单列表。
 * size 超过上限 50 时前端先行钳制，避免无谓的下游裁剪。
 */
export async function getThirdPartyOrders(
  query: ThirdPartyOrdersQuery = {}
): Promise<ThirdPartyOrdersPage> {
  const { page = 1, size = 20 } = query;
  const params: Record<string, string | number> = {
    page,
    size: Math.min(size, MAX_PAGE_SIZE),
  };
  if (query.merchantOrderId) params.merchantOrderId = query.merchantOrderId;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;

  const payload = await get<RawThirdPartyOrdersResponse>(
    "/third-party-orders",
    { params }
  );
  const rows = Array.isArray(payload?.orders) ? payload.orders : [];
  return {
    orders: rows,
    total: typeof payload?.total === "number" ? payload.total : rows.length,
    error: payload?.error || null,
  };
}

/**
 * GET /third-party-orders/export：实时代理下游订单并导出 CSV。
 * from/to 为必填 ISO instant，跨度不得超过 MAX_EXPORT_RANGE_DAYS。返回 Blob 供下载。
 *
 * 错误响应（后端）：
 *   - 400：from/to 缺失、格式非法或跨度超限
 *   - 502：下游联盟订单接口查询失败
 * responseType 为 blob 时错误体也是 Blob，这里读取其文本尝试解析 { message }，
 * 解析失败则按状态码回落到友好文案。
 */
export async function exportThirdPartyOrdersCsv(params: {
  from: string;
  to: string;
  merchantOrderId?: string;
}): Promise<Blob> {
  const query: Record<string, string> = {
    from: params.from,
    to: params.to,
  };
  if (params.merchantOrderId) query.merchantOrderId = params.merchantOrderId;

  try {
    const res = await request.get("/third-party-orders/export", {
      params: query,
      responseType: "blob",
    });
    // 响应拦截器已返回 response.data，这里即为 Blob
    return res as unknown as Blob;
  } catch (err) {
    throw await normalizeExportError(err);
  }
}

/** 把导出错误（可能携带 Blob 错误体）转成可直接展示的 Error */
async function normalizeExportError(err: unknown): Promise<Error> {
  const anyErr = err as {
    status?: number;
    message?: string;
    // ApiError 上不带 response，这里兼容原始 AxiosError 形态
    response?: { data?: unknown; status?: number };
  };
  const status = anyErr?.status ?? anyErr?.response?.status;

  // 尝试从 Blob 错误体解析后端 { message }
  const data = anyErr?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.message === "string") {
        return new Error(parsed.message);
      }
    } catch {
      /* 解析失败，走状态码回落 */
    }
  }

  if (status === 400) {
    return new Error(
      "Invalid export range. Provide a valid date range within the allowed span."
    );
  }
  if (status === 502) {
    return new Error(
      "The affiliate order service is unavailable. Export could not be completed."
    );
  }
  return new Error(anyErr?.message || "Export failed. Please try again later.");
}
